import { STAGE } from '../../design/lab/palette';
import React from 'react';
import BlobBot from './BlobBot';
import { BotState, BotVariant } from './motionSystem';
import type { OrbState } from 'thinking-orbs';
import AgentActivity from './AgentActivity';

export interface AgentStatusProps {
    state: BotState;
    name: string;
    color: string;
    variant?: BotVariant;
    /** What it is doing right now — e.g. the tool it just reached for */
    detail?: string | null;
    size?: number;
    activity?: OrbState;
}

const mono: React.CSSProperties = {
    fontFamily: "'JetBrains Mono','Fira Code',monospace",
};

/**
 * What each state MEANS, in the second person. The avatar carries the feeling;
 * this carries the fact. Together they remove the ambiguity the whole motion
 * system exists to remove — is it working, or is it stuck?
 */
const READING: Record<BotState, string> = {
    idle: 'waiting on you',
    curious: 'listening',
    thinking: 'working out what to do',
    working: 'running a tool',
    waiting: 'waiting on a slow response',
    blocked: 'stuck — see the error above',
    done: 'answered',
};

/**
 * AgentStatus — a persistent, legible readout of what the agent is doing.
 *
 * The avatar had been living at 20px in a titlebar, which is too small to read
 * as motion: you could not actually watch the lifecycle you were being told
 * about. At this size the squash, the tilt and the orbiting satellites are
 * visible, and the label names the state so the motion is learnable rather than
 * merely decorative.
 */
const AgentStatus: React.FC<AgentStatusProps> = ({
    state, name, color, variant = 'blob', detail, size = 42, activity,
}) => {
    const busy = state !== 'idle' && state !== 'done';

    return (
        <div className="agent-status" style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '10px 14px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(255,255,255,0.02)',
            flexShrink: 0,
        }}>
            <BlobBot color={color} size={size} state={state} variant={variant} />

            <div className="agent-status__copy" style={{ minWidth: 0, flex: 1 }}>
                <div className="agent-status__heading" style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                    <span style={{ ...mono, fontSize: '12px', fontWeight: 700, color: STAGE.ink }}>
                        {name}
                    </span>
                    <span style={{
                        ...mono, fontSize: '11px',
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        color: state === 'blocked' ? STAGE.red : busy ? color : STAGE.muted,
                        transition: 'color 0.3s',
                    }}>
                        <AgentActivity state={state} work={activity} theme="dark" />
                        {state}
                    </span>
                </div>
                <div className="agent-status__detail" style={{
                    ...mono, fontSize: '10.5px', color: STAGE.muted, marginTop: '2px',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {detail || READING[state]}
                </div>
            </div>
        </div>
    );
};

export default AgentStatus;
